"use client";

import { tabSwitched } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { RootState } from "@/store/store";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Program Structure 1042 — BDU Programme Syllabus.
 *
 * Port of BDU v1 `components/Degree/ProgramStructure.tsx` →
 * `SyllabusWithoutSpecialisation` → `hoc/ProgrammeSyllabus` + `ui/SyllabusTable` +
 * `ui/ElectiveCourseTable`.
 *
 * LAYOUT — the section is a flex row of two children over one shared background image:
 * a `w-full` wrapper whose inner panel is tinted (a flat scrim below 768px, the solid
 * panel colour above), and a `tab:w-[800px]` column that leaves the art uncovered. Both
 * children shrink, so the split is proportional rather than a fixed half — that is the
 * reference's own behaviour and is reproduced by keeping its markup.
 *
 * TWO GRADIENTS, and they are NOT the same colour. The rule under the heading is BDU's
 * blue `bg-fee-header` (gradient 1/2); the brochure CTA and the "as per elective" pill
 * are its red `bg-button-gradient-red` (gradient 3/4). Above 768px the reference swaps
 * the blue rule for a flat white one.
 *
 * DATA — flat metadata rows, the same shape Program Structure 4 uses, so nothing new is
 * stored: `semester_name` groups a row into a tab and `credits` is its trailing value;
 * a row with `elective_name` instead joins that elective group and shows its own
 * `subject_semester`. A semester marked `semester_is_elective_based` renders the pill,
 * hides its subject list and shows the elective picker — what BDU's `is_elective_based`
 * flag does.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here (this app remaps `md:` to 1230px).
 *
 * GRADIENT TEXT — `bg-clip-text` alone leaves the label INVISIBLE in WebKit because
 * this app's Lightning CSS pipeline emits no `-webkit-` prefixes, so
 * `WebkitBackgroundClip` is set inline as well.
 */

export interface ProgramStructure1042Row {
  id?: string | number;
  semester_name?: string;
  /** Marks the whole semester as "as per elective" (subject list hidden). */
  semester_is_elective_based?: boolean;
  subject_name?: string;
  credits?: string | number;
  elective_name?: string;
  subject_semester?: string;
  priority?: number;
  status?: boolean;
}

interface ProgramStructure1042Data {
  metadata?: ProgramStructure1042Row[];
  title?: string;
  title_text_color?: string;
  /** Background art (both halves) */
  section_background_image?: string;
  section_background_image_alt?: string;
  /** Left panel fill from 768px up */
  container_background_color?: string;
  /** Left panel scrim below 768px */
  header_section_color?: string;
  /** Subject / elective card surface */
  subject_container_color?: string;
  /** Tab labels */
  module_active_text_color?: string;
  module_inactive_text_color?: string;
  /** Elective card inner surfaces */
  content_panel_background_color?: string;
  subjects_column_label?: string;
  credits_column_label?: string;
  electives_tab_label?: string;
  electives_semester_label?: string;
  electives_empty_message?: string;
  /** Blue rule under the heading (below 768px) */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Red accent — brochure CTA + "as per elective" pill */
  gradient_color_3?: string;
  gradient_color_4?: string;
  /** Pill background behind the accent text */
  card_background_color?: string;
  brochure_cta_text?: string;
  brochure_cta_text_color?: string;
  /** Off = link straight to the bound course's brochure instead of the lead form. */
  brochure_cta_is_cta?: boolean;
  status?: boolean;
}

interface SubjectItem {
  name: string;
  /** Credits on a semester row, the subject's own semester on an elective row. */
  right: string;
}

interface ElectiveGroup {
  name: string;
  priority: number;
  subjects: SubjectItem[];
}

interface SemesterTab {
  name: string;
  priority: number;
  isElectiveBased: boolean;
  subjects: SubjectItem[];
}

/** The 12x12 chevron the reference loads from `/iconButtons/chevron-down.svg`. */
const ChevronDown = ({ open }: { open: boolean }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    className={cn("shrink-0 transition-transform", open && "rotate-180")}
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

/** "4" → "4 credits", "1" → "1 credit", 0 / blank → "" (the reference hides zeroes). */
function formatCredits(credits: string | number | undefined): string {
  const raw = String(credits ?? "").trim();
  if (!raw) return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  if (n <= 0) return "";
  return `${n} ${n === 1 ? "credit" : "credits"}`;
}

function buildTabs(rows: ProgramStructure1042Row[]): {
  tabs: SemesterTab[];
  electives: ElectiveGroup[];
} {
  const bySemester = new Map<string, SemesterTab>();
  const byElective = new Map<string, ElectiveGroup>();

  rows
    .filter((r) => r && r.status !== false)
    .slice()
    .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0))
    .forEach((r) => {
      const priority = Number(r.priority) || 0;
      const subjectName = String(r.subject_name ?? "").trim();
      const electiveName = String(r.elective_name ?? "").trim();

      if (electiveName) {
        let group = byElective.get(electiveName);
        if (!group) {
          group = { name: electiveName, priority, subjects: [] };
          byElective.set(electiveName, group);
        }
        if (subjectName) {
          group.subjects.push({
            name: subjectName,
            right: String(r.subject_semester ?? "").trim(),
          });
        }
        return;
      }

      const semesterName = String(r.semester_name ?? "").trim();
      if (!semesterName) return;

      let tab = bySemester.get(semesterName);
      if (!tab) {
        tab = {
          name: semesterName,
          priority,
          isElectiveBased: false,
          subjects: [],
        };
        bySemester.set(semesterName, tab);
      }
      if (r.semester_is_elective_based) tab.isElectiveBased = true;
      if (subjectName) {
        tab.subjects.push({
          name: subjectName,
          right: formatCredits(r.credits),
        });
      }
    });

  const byPriority = <T extends { priority: number }>(a: T, b: T) =>
    a.priority - b.priority;

  return {
    tabs: [...bySemester.values()].sort(byPriority),
    electives: [...byElective.values()].sort(byPriority),
  };
}

const ProgramStructure1042 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as
    | ProgramStructure1042Data
    | undefined;
  const dispatch = useDispatch();

  const {
    title = "Programme Syllabus",
    title_text_color = "#FFFFFF",
    section_background_image = "",
    container_background_color = "#394E63",
    header_section_color = "rgba(4, 12, 20, 0.63)",
    subject_container_color = "#FFFFFF",
    module_active_text_color = "#FFFFFF",
    module_inactive_text_color = "#FFFFFF",
    content_panel_background_color = "#F0F0F0",
    subjects_column_label = "Subjects",
    credits_column_label = "Semester",
    electives_tab_label = "Elective courses",
    electives_semester_label = "as per elective",
    electives_empty_message = "There are no subjects in this elective",
    gradient_color_1 = "#1F3D56",
    gradient_color_2 = "#7CB5E6",
    gradient_color_3 = "#FF0013",
    gradient_color_4 = "#990013",
    card_background_color = "#FEF0F3",
    brochure_cta_text = "Download Brochure",
    brochure_cta_text_color = "#FFFFFF",
  } = sectionData || {};

  const brochureIsCta = isCmsShowFlagOn(sectionData?.brochure_cta_is_cta, true);
  /*
   * The reference takes the brochure straight off the programme record. The
   * equivalent here is the page's bound course (same source Program Structure 4
   * uses), so there is no brochure-URL column on this variant.
   */
  const brochureUrl = useSelector(
    (state: RootState) => state.globalslice?.course?.brochure || "",
  );

  const { tabs, electives } = useMemo(
    () =>
      buildTabs(Array.isArray(sectionData?.metadata) ? sectionData.metadata : []),
    [sectionData?.metadata],
  );

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedElective, setSelectedElective] = useState(0);
  const [electiveMenuOpen, setElectiveMenuOpen] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const electiveMenuRef = useRef<HTMLDivElement | null>(null);

  // Radix's DropdownMenu closes on outside click; this picker gets the same.
  useEffect(() => {
    if (!electiveMenuOpen) return undefined;
    const close = (e: MouseEvent) => {
      if (!electiveMenuRef.current?.contains(e.target as Node)) {
        setElectiveMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [electiveMenuOpen]);

  const active = tabs[Math.min(selectedTab, Math.max(0, tabs.length - 1))];
  const activeElective = electives[selectedElective];

  const onSelectTab = useCallback(
    (index: number) => {
      setSelectedTab(index);
      tabRefs.current[index]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
      tabSwitched({
        section: "program_structure_1042",
        component: "semester",
        label: tabs[index]?.name || `tab_${index}`,
      });
    },
    [tabs],
  );

  const handleBrochureClick = useCallback(() => {
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
        downloadLink: brochureUrl,
      }),
    );
  }, [brochureUrl, dispatch]);

  if (!sectionData?.status || tabs.length === 0) return null;

  const bgStyle = section_background_image
    ? {
        backgroundImage: `url('${section_background_image}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  const accentGradient = `linear-gradient(to right, ${gradient_color_3}, ${gradient_color_4})`;
  const ruleGradient = `linear-gradient(90deg, ${gradient_color_1} 0%, ${gradient_color_2} 100%)`;
  const clipText = {
    backgroundImage: accentGradient,
    WebkitBackgroundClip: "text" as const,
    backgroundClip: "text" as const,
  };
  const showElectiveCard =
    Boolean(active?.isElectiveBased) && electives.length > 0;
  const showSubjects =
    !active?.isElectiveBased && (active?.subjects.length ?? 0) > 0;

  return (
    <section className="relative flex w-full" style={bgStyle}>
      <div className="w-full tab:relative tab:top-0 tab:block tab:h-full">
        <div
          // `relative` is load-bearing: it scopes the two layers below to this column.
          // Without it they resolve against the section and paint over the background
          // art in the right half too.
          className="relative flex items-center justify-center tab:h-full tab:px-[38px]"
        >
          {/*
            The reference stacks BOTH of these from 768px up, and the ORDER matters.
            `bg-programme-syllabus` is a background-IMAGE (a flat rgba(4,12,20,0.63)
            gradient) while `md:bg-blue-150` is a background-COLOR, so the scrim paints
            OVER the panel fill — the desktop panel is the composite (~#182431), not the
            raw fill. Rendering the fill on top instead washes the column out, which is
            exactly what it looked like before this was corrected.

            Layer 1: the panel fill, from 768px up only.
          */}
          <div
            aria-hidden
            className="absolute inset-0 hidden size-full tab:block"
            style={{ backgroundColor: container_background_color }}
          />
          {/* Layer 2: the scrim, at every width — over the fill above, over the art below. */}
          <div
            aria-hidden
            className="absolute inset-0 size-full"
            style={{ backgroundColor: header_section_color }}
          />
          <div className="relative flex flex-col gap-[16px] px-[19px] py-[62px] tab:h-fit tab:w-[500px] tab:gap-[20px] tab:px-[2px] tab:py-[32px] lg:w-[614px]">
            <div className="flex size-fit flex-col gap-[12px] text-[22px] font-bold leading-[28px] tab:gap-[14px] tab:text-[28px] tab:font-medium tab:leading-[36px]">
              <h2 style={{ color: title_text_color }}>{title}</h2>
              {/* Below 768px the rule is the blue gradient; above it, a flat bar. */}
              <div
                className="block h-[2px] w-[250px] tab:hidden"
                style={{ backgroundImage: ruleGradient }}
              />
              <div
                className="hidden h-[2px] w-[250px] tab:block tab:h-[3px] tab:w-[334px]"
                style={{ backgroundColor: title_text_color }}
              />
            </div>

            <div className="flex flex-col gap-[8px] tab:gap-[10px]">
              {/* Semester tab strip */}
              <div className="flex w-[324px] gap-[28px] overflow-x-auto [scrollbar-width:none] tab:w-full tab:gap-[32px] [&::-webkit-scrollbar]:hidden">
                {tabs.map((t, index) => {
                  const isActive = index === selectedTab;
                  return (
                    <button
                      key={t.name}
                      type="button"
                      ref={(el) => {
                        tabRefs.current[index] = el;
                      }}
                      onClick={() => onSelectTab(index)}
                      className={cn(
                        "flex cursor-pointer items-center justify-center gap-[3px] whitespace-nowrap tab:gap-[4px]",
                        isActive && "border-b-[0.8px]",
                      )}
                      style={{
                        borderColor: isActive
                          ? module_active_text_color
                          : undefined,
                      }}
                    >
                      <span
                        className="text-[14px] font-medium leading-[22px] tab:leading-[28px]"
                        style={{
                          color: isActive
                            ? module_active_text_color
                            : module_inactive_text_color,
                        }}
                      >
                        {t.name}
                      </span>
                      {t.isElectiveBased && (
                        <span
                          className="rounded-[12px]"
                          style={{ backgroundColor: card_background_color }}
                        >
                          <span
                            className="block bg-clip-text px-[4px] text-[12px] text-transparent tab:h-[16px] tab:font-medium tab:leading-[16px]"
                            style={clipText}
                          >
                            {electives_semester_label}
                          </span>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Subject card */}
              {showSubjects && (
                <div
                  className="flex flex-col gap-[12px] rounded-[4px] p-[10px] tab:gap-[16px] tab:rounded-[6px] tab:p-[16px]"
                  style={{ backgroundColor: subject_container_color }}
                >
                  {active.subjects.map((subject, i) => (
                    <div
                      key={`${subject.name}-${i}`}
                      className="flex justify-between px-[10px] tab:px-[12px]"
                    >
                      <p className="text-[12px] font-medium leading-[20px] tab:text-[16px] tab:leading-[24px]">
                        {subject.name}
                      </p>
                      {subject.right && (
                        <p className="text-[10px] font-normal leading-[20px] text-[#4E4E4ECC] tab:text-[12px] tab:leading-[24px]">
                          {subject.right}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Elective picker */}
              {showElectiveCard && (
                <div
                  className="flex w-full flex-col gap-[14px] rounded-[6px] px-[11px] py-[10px] tab:px-[14px] tab:py-[12px]"
                  style={{ backgroundColor: subject_container_color }}
                >
                  <div className="h-[23px] w-fit border-b border-[#5B5B5B] text-[12px] font-bold leading-[22px] tab:h-[28px] tab:text-[16px] tab:leading-[28px]">
                    {electives_tab_label}
                  </div>

                  <div className="flex flex-col gap-[8px] tab:gap-[10px]">
                    <div
                      ref={electiveMenuRef}
                      className="relative flex w-[184px] flex-col gap-[6px] tab:w-[228px]"
                    >
                      <button
                        type="button"
                        onClick={() => setElectiveMenuOpen((open) => !open)}
                        aria-expanded={electiveMenuOpen}
                        className="flex w-full items-center justify-between rounded-[4px] px-[8px] py-[6px] text-[13px] leading-[19px] text-black"
                        style={{
                          backgroundColor: content_panel_background_color,
                        }}
                      >
                        {activeElective?.name}
                        <ChevronDown open={electiveMenuOpen} />
                      </button>
                      {electiveMenuOpen && (
                        <div
                          className="absolute left-0 top-full z-10 mt-2 w-[184px] rounded-[8px] shadow-[0px_4px_10.9px_0px_#00000040] tab:w-[228px]"
                          style={{ backgroundColor: subject_container_color }}
                        >
                          {electives.map((elective, index) => (
                            <button
                              key={elective.name}
                              type="button"
                              className="block w-full cursor-pointer p-[10px] text-left text-[13px] leading-[19px]"
                              onClick={() => {
                                setSelectedElective(index);
                                setElectiveMenuOpen(false);
                                tabSwitched({
                                  section: "program_structure_1042",
                                  component: "elective",
                                  label: elective.name,
                                });
                              }}
                            >
                              {elective.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div
                      className="flex flex-col gap-[10px] rounded-[5px] p-[10px] tab:gap-[12px] tab:rounded-[6px] tab:p-[12px]"
                      style={{
                        backgroundColor: content_panel_background_color,
                      }}
                    >
                      <div
                        className="flex justify-between rounded-[5px] px-[10px] py-[4px] tab:rounded-[6px] tab:px-[12px]"
                        style={{ backgroundColor: subject_container_color }}
                      >
                        <p className="text-[11px] font-medium leading-[20px] text-[#222023] tab:text-[14px] tab:leading-[24px]">
                          {subjects_column_label}
                        </p>
                        <p
                          className="bg-clip-text text-[11px] font-medium leading-[20px] text-transparent tab:text-[14px] tab:leading-[24px]"
                          style={clipText}
                        >
                          {credits_column_label}
                        </p>
                      </div>

                      {activeElective && activeElective.subjects.length > 0 ? (
                        activeElective.subjects.map((subject, i) => (
                          <div
                            className="flex justify-between tab:px-[12px]"
                            key={`${subject.name}-${i}`}
                          >
                            <p className="text-[11px] font-medium leading-[20px] tab:text-[14px] tab:leading-[24px]">
                              {subject.name}
                            </p>
                            {subject.right && (
                              <p className="text-[10px] font-normal leading-[20px] text-[#4E4E4ECC] tab:text-[12px] tab:leading-[24px]">
                                {subject.right}
                              </p>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="text-[11px] leading-[20px] tab:text-[14px] tab:leading-[24px]">
                          {electives_empty_message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {brochure_cta_text &&
              (brochureIsCta || !brochureUrl ? (
                <button
                  type="button"
                  onClick={handleBrochureClick}
                  className="cursor-pointer rounded-[4px] py-[8px] text-[12px] font-medium tab:py-[10px] tab:text-[14px] tab:font-semibold"
                  style={{
                    backgroundImage: accentGradient,
                    color: brochure_cta_text_color,
                  }}
                >
                  {brochure_cta_text}
                </button>
              ) : (
                <Link
                  href={normalizeHref(brochureUrl)}
                  className="cursor-pointer rounded-[4px] py-[8px] text-center text-[12px] font-medium tab:py-[10px] tab:text-[14px] tab:font-semibold"
                  style={{
                    backgroundImage: accentGradient,
                    color: brochure_cta_text_color,
                  }}
                >
                  {brochure_cta_text}
                </Link>
              ))}
          </div>
        </div>
      </div>

      {/* Right half — the same art, uncovered. Decorative, so no alt text. */}
      <div
        className="hidden w-full items-center justify-center object-cover tab:flex tab:w-[800px] tab:flex-col tab:py-[60px]"
        style={bgStyle}
        aria-hidden
      />
    </section>
  );
};

export default ProgramStructure1042;
